import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, IndianRupee, Download, CheckCircle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { toast } from 'sonner';

const PayoutManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [expandedOwner, setExpandedOwner] = useState(null);
  const [stats, setStats] = useState({
    total_earnings: 0,
    total_platform_fee: 0,
    total_payout: 0,
    pending_payouts: 0,
    completed_payouts: 0,
    owner_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refunds, setRefunds] = useState([]);

  useEffect(() => {
    fetchPayouts();
    fetchRefunds();
  }, []);

  const fetchRefunds = async () => {
    try {
      const res = await axios.get(`${API}/admin/deposit-refunds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefunds(res.data || []);
    } catch (e) {
      setRefunds([]);
    }
  };

  const markRefundPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/deposit-refunds/${id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Marked as paid to bank');
      fetchRefunds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark paid');
    }
  };

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data || [];
      setPayouts(data);

      const totalEarnings = data.reduce((sum, p) => sum + (p.total_earnings || 0), 0);
      const totalFee = data.reduce((sum, p) => sum + (p.total_platform_fee || 0), 0);
      const totalPayout = data.reduce((sum, p) => sum + (p.total_payout || 0), 0);
      const pending = data
        .filter((p) => p.status === 'pending')
        .reduce((sum, p) => sum + (p.total_payout || 0), 0);
      const completed = data
        .filter((p) => p.status === 'completed')
        .reduce((sum, p) => sum + (p.total_payout || 0), 0);

      setStats({
        total_earnings: totalEarnings,
        total_platform_fee: totalFee,
        total_payout: totalPayout,
        pending_payouts: pending,
        completed_payouts: completed,
        owner_count: data.length,
      });
    } catch (error) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (ownerId) => {
    try {
      await axios.patch(
        `${API}/admin/payouts/${ownerId}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payout marked as paid');
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to update payout');
    }
  };

  const handleExportCSV = () => {
    if (payouts.length === 0) {
      toast.error('No data to export');
      return;
    }

    const csvRows = [
      ['Owner Name', 'Total Earnings', 'Platform Fee (10%)', 'Payout Due', 'Status', 'Items'].join(','),
    ];

    payouts.forEach((p) => {
      csvRows.push(
        [
          p.owner_name,
          (p.total_earnings || 0).toFixed(2),
          (p.total_platform_fee || 0).toFixed(2),
          (p.total_payout || 0).toFixed(2),
          p.status || 'pending',
          p.items?.length || 0,
        ].join(',')
      );
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payouts.csv';
    a.click();
    toast.success('CSV exported');
  };

  const toggleOwnerExpand = (ownerId) => {
    setExpandedOwner(expandedOwner === ownerId ? null : ownerId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="text-xl font-semibold text-gray-700">Loading payouts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/super-admin')} className="flex items-center space-x-2">
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-4xl font-bold" style={{ color: 'var(--foreground)' }}>Payouts & Earnings</h1>
          <Button onClick={handleExportCSV} style={{ backgroundColor: 'var(--accent)', color: 'white' }} className="hover:opacity-90">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-6 max-w-3xl">
          Lender earnings stay in this app as a ledger (30% of rental). Marking paid records that you transferred the money — the site does not send bank payouts by itself. Security deposit withdrawals are also listed here until you pay them to the user’s bank.
        </p>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold mb-2">Deposit refunds waiting for bank payout</h2>
          {refunds.length === 0 ? (
            <p className="text-sm text-gray-500">No deposit withdrawals pending.</p>
          ) : (
            <div className="space-y-2">
              {refunds.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-3">
                  <div>
                    <p className="font-medium">{r.user_name || r.user_email || 'User'} · ₹{Math.round(r.amount || 0)}</p>
                    <p className="text-xs text-gray-500">{r.status} · {r.user_email || ''} {r.user_phone || ''}</p>
                  </div>
                  {r.status !== 'paid' ? (
                    <Button size="sm" onClick={() => markRefundPaid(r.id)}>Mark paid to bank</Button>
                  ) : (
                    <span className="text-sm text-green-700">Paid</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="p-3 rounded-lg inline-block mb-4" style={{ backgroundColor: 'var(--card)' }}>
              <IndianRupee className="h-6 w-6" style={{ color: 'var(--primary)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--foreground)' }}>Total Earnings</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--foreground)' }}>₹{Math.round(stats.total_earnings)}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="p-3 rounded-lg inline-block mb-4" style={{ backgroundColor: 'var(--card)' }}>
              <IndianRupee className="h-6 w-6" style={{ color: 'var(--accent)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--foreground)' }}>Pending Payouts</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--accent)' }}>₹{Math.round(stats.pending_payouts)}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="p-3 rounded-lg inline-block mb-4" style={{ backgroundColor: 'var(--card)' }}>
              <CheckCircle className="h-6 w-6" style={{ color: 'green' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--foreground)' }}>Completed Payouts</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--foreground)' }}>₹{Math.round(stats.completed_payouts)}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="p-3 rounded-lg inline-block mb-4" style={{ backgroundColor: 'var(--card)' }}>
              <User className="h-6 w-6" style={{ color: 'var(--primary)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--foreground)' }}>Book Owners</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--foreground)' }}>{stats.owner_count}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {payouts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No payout data available</div>
          ) : (
            payouts.map((payout) => (
              <div key={payout.owner_id || payout.id} className="border-b last:border-b-0">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleOwnerExpand(payout.owner_id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="p-2 rounded-full" style={{ backgroundColor: 'var(--card)' }}>
                      <User className="h-5 w-5" style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{payout.owner_name}</p>
                      <p className="text-sm text-gray-500">{payout.items?.length || 0} order item(s)</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-500">Earnings</p>
                      <p className="font-semibold">₹{(payout.total_earnings || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-500">Fee (10%)</p>
                      <p className="text-red-600">-₹{(payout.total_platform_fee || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Payout Due</p>
                      <p className="font-bold" style={{ color: 'var(--accent)' }}>₹{(payout.total_payout || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      {payout.status === 'pending' ? (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--card)', color: 'var(--accent)' }}>
                          Pending
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Paid</span>
                      )}
                    </div>
                    {expandedOwner === payout.owner_id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedOwner === payout.owner_id && payout.items && payout.items.length > 0 && (
                  <div className="border-t px-4 py-3" style={{ backgroundColor: 'var(--card)' }}>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>Order Items</h4>
                      {payout.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsPaid(payout.owner_id);
                          }}
                          style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                          className="hover:opacity-90"
                        >
                          Mark as Paid
                        </Button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-600">
                            <th className="pb-2">Book</th>
                            <th className="pb-2">Amount</th>
                            <th className="pb-2">Fee</th>
                            <th className="pb-2">Payout</th>
                            <th className="pb-2">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {payout.items.map((item, index) => (
                            <tr key={index}>
                              <td className="py-2 font-medium" style={{ color: 'var(--foreground)' }}>{item.book_title}</td>
                              <td className="py-2">₹{(item.amount || 0).toFixed(2)}</td>
                              <td className="py-2 text-red-600">-₹{(item.platform_fee || 0).toFixed(2)}</td>
                              <td className="py-2 font-semibold" style={{ color: 'var(--accent)' }}>₹{(item.payout_amount || 0).toFixed(2)}</td>
                              <td className="py-2 text-gray-500">
                                {item.date ? new Date(item.date).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutManagement;
